import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { ReactComponent as DeleteIcon } from "../../../assects/svg/client_details/delete.svg";
import CustomDropdown from "../../Ui_Components/CustomDropdown";
import {
  getAllExerciseTypeList,
  createExercise,
} from "../../../services/subPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import { sendPlanNotificationToUser } from "../../../utils/oneSignalNotification";
import { Button } from "../../Ui_Components/Button";

interface ExercisePlanCreatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  userId: number;
  onShowToast?: (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number
  ) => void;
}

interface ExerciseEntry {
  id: string;
  exerciseId: number | null; // ← stores the exercise_id from API
  type: string;
  time: {
    hour: string;
    minute: string;
    period: string;
  };
  duration: {
    hour: string;
    minute: string;
  };
  levelValue: string;
  totalCalories: string;
  comment: string;
}

// ─── ExerciseRow ─────────────────────────────────────────────────────────────
interface ExerciseRowProps {
  exercise: ExerciseEntry;
  totalRows: number;
  exerciseTypeOptions: { value: string; label: string; exercise_id: number }[];
  isFetchingTypes: boolean;
  hourOptions: { value: string; label: string }[];
  minuteOptions: { value: string; label: string }[];
  periodOptions: { value: string; label: string }[];
  durationHourOptions: { value: string; label: string }[];
  durationMinuteOptions: { value: string; label: string }[];
  onExerciseChange: (
    id: string,
    field: string,
    value: string,
    subField?: string
  ) => void;
  onRemove: (id: string) => void;
  onShowToast?: (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number
  ) => void;
}

const ExerciseRow: React.FC<ExerciseRowProps> = ({
  exercise,
  totalRows,
  exerciseTypeOptions,
  isFetchingTypes,
  hourOptions,
  minuteOptions,
  periodOptions,
  durationHourOptions,
  durationMinuteOptions,
  onExerciseChange,
  onRemove,
  onShowToast,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isRowEmpty = totalRows === 1 && !exercise.type;

  const handleDeleteClick = () => {
    if (isRowEmpty) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onRemove(exercise.id);
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // ── Confirmation bar ────────────────────────────────────────────────────────
  if (showDeleteConfirm) {
    return (
      <div className="flex items-center justify-between mb-2 px-3 py-[6px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md col-span-full">
        <span className="text-sm text-[#1F1F1F] truncate mr-4">
          Delete{" "}
          <span className="font-medium">
            "{exercise.type || "this exercise"}"
          </span>
          ?
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="danger"
            size="xs"
            onClick={handleConfirmDelete}
            className="whitespace-nowrap"
          >
            Confirm delete
          </Button>
          <button
            onClick={handleCancelDelete}
            className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-1 rounded border-[0.5px] border-[#1F1F1F]"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // ── Normal row ──────────────────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-[4fr_4fr_3fr_auto] gap-6 items-center">
      {/* Exercise type */}
      <CustomDropdown
        options={exerciseTypeOptions}
        value={exercise.type}
        onChange={(value) => onExerciseChange(exercise.id, "type", value)}
        placeholder={isFetchingTypes ? "Loading..." : "Search or select type"}
        searchable={true}
        disabled={isFetchingTypes}
      />

      {/* Time */}
      <div className="grid grid-cols-3 gap-2">
        <CustomDropdown
          options={hourOptions}
          value={exercise.time.hour}
          onChange={(value) =>
            onExerciseChange(exercise.id, "time", value, "hour")
          }
          placeholder="Hr"
          searchable={false}
        />
        <CustomDropdown
          options={minuteOptions}
          value={exercise.time.minute}
          onChange={(value) =>
            onExerciseChange(exercise.id, "time", value, "minute")
          }
          placeholder="Min"
          searchable={false}
        />
        <CustomDropdown
          options={periodOptions}
          value={exercise.time.period}
          onChange={(value) =>
            onExerciseChange(exercise.id, "time", value, "period")
          }
          placeholder="AM"
          searchable={false}
        />
      </div>

      {/* Duration */}
      <div className="grid grid-cols-2 gap-2">
        <CustomDropdown
          options={durationHourOptions}
          value={exercise.duration.hour}
          onChange={(value) =>
            onExerciseChange(exercise.id, "duration", value, "hour")
          }
          placeholder="Hr"
          searchable={false}
        />
        <CustomDropdown
          options={durationMinuteOptions}
          value={exercise.duration.minute}
          onChange={(value) =>
            onExerciseChange(exercise.id, "duration", value, "minute")
          }
          placeholder="Min"
          searchable={false}
        />
      </div>

      {/* Delete button */}
      <div className="flex items-center justify-center">
        <button
          onClick={handleDeleteClick}
          disabled={isRowEmpty}
          title={isRowEmpty ? "Nothing to delete" : "Delete row"}
          className={`transition-colors ${
            isRowEmpty
              ? "text-[#D1D1D6] cursor-not-allowed"
              : "text-[#FF3B30] hover:text-red-700 cursor-pointer"
          }`}
        >
          <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );
};

// ─── Main Popup ───────────────────────────────────────────────────────────────
const ExercisePlanCreatePopup: React.FC<ExercisePlanCreatePopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userId,
  onShowToast,
}) => {
  const { user } = useAuth();
  const nutristId = user?.nutrist_id;
  const [isCreating, setIsCreating] = useState(false);

  // ── Exercise type options fetched from API ──────────────────────────────────
  const [exerciseTypeOptions, setExerciseTypeOptions] = useState<
    { value: string; label: string; exercise_id: number }[]
  >([]);
  const [isFetchingTypes, setIsFetchingTypes] = useState(false);

  // ── Fetch exercise types when popup opens ───────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const fetchExerciseTypes = async () => {
      setIsFetchingTypes(true);
      try {
        const json = await getAllExerciseTypeList();
        if (json.success === "1" && Array.isArray(json.exercises)) {
          const options = json.exercises.map(
            (ex: { exercise_id: number; exercise_type: string }) => ({
              value: ex.exercise_type.trim(),
              label: ex.exercise_type.trim(),
              exercise_id: ex.exercise_id,
            })
          );
          setExerciseTypeOptions(options);
        } else {
          onShowToast?.(
            "error",
            "Failed to load exercise types",
            json.message || "Could not retrieve exercise list."
          );
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "An unexpected error occurred.";
        onShowToast?.("error", "Error loading exercise types", message);
      } finally {
        setIsFetchingTypes(false);
      }
    };

    fetchExerciseTypes();
  }, [isOpen]);

  const [exercises, setExercises] = useState<ExerciseEntry[]>([
    {
      id: "1",
      exerciseId: null,
      type: "",
      time: { hour: "", minute: "", period: "" },
      duration: { hour: "", minute: "" },
      levelValue: "",
      totalCalories: "",
      comment: "",
    },
  ]);

  const newEmptyExercise = (): ExerciseEntry => ({
    id: String(Date.now()),
    exerciseId: null,
    type: "",
    time: { hour: "", minute: "", period: "" },
    duration: { hour: "", minute: "" },
    levelValue: "",
    totalCalories: "",
    comment: "",
  });

  // ── Dropdown option arrays ──────────────────────────────────────────────────
  const hourOptions = Array.from({ length: 13 }, (_, i) => ({
    value: String(i).padStart(2, "0"),
    label: String(i).padStart(2, "0"),
  }));

  const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
    value: String(i).padStart(2, "0"),
    label: String(i).padStart(2, "0"),
  }));

  const periodOptions = [
    { value: "AM", label: "AM" },
    { value: "PM", label: "PM" },
  ];

  const durationHourOptions = Array.from({ length: 13 }, (_, i) => ({
    value: String(i).padStart(2, "0"),
    label: String(i).padStart(2, "0"),
  }));

  const durationMinuteOptions = Array.from({ length: 60 }, (_, i) => ({
    value: String(i).padStart(2, "0"),
    label: String(i).padStart(2, "0"),
  }));

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleAddMore = () => {
    setExercises([...exercises, newEmptyExercise()]);
  };

  const handleExerciseChange = (
    id: string,
    field: string,
    value: string,
    subField?: string
  ) => {
    setExercises(
      exercises.map((exercise) => {
        if (exercise.id !== id) return exercise;

        if (subField && (field === "time" || field === "duration")) {
          const currentField = exercise[field as "time" | "duration"] as {
            hour: string;
            minute: string;
            period?: string;
          };
          const updatedField = { ...currentField, [subField]: value };

          if (updatedField.hour === "00" && updatedField.minute === "00") {
            onShowToast?.(
              "warning",
              "Invalid value",
              `${
                field === "time" ? "Time" : "Duration"
              } cannot be 00:00. Please select a valid value.`
            );
            return exercise;
          }

          return { ...exercise, [field]: updatedField };
        }

        // ── When exercise type changes, also store the matching exercise_id ──
        if (field === "type") {
          const matched = exerciseTypeOptions.find(
            (opt) => opt.value === value
          );
          return {
            ...exercise,
            type: value,
            exerciseId: matched ? matched.exercise_id : null,
          };
        }

        return { ...exercise, [field]: value };
      })
    );
  };

  const handleRemoveExercise = (id: string) => {
    const remaining = exercises.filter((e) => e.id !== id);
    if (remaining.length === 0) {
      setExercises([newEmptyExercise()]);
    } else {
      setExercises(remaining);
    }
  };

  const isFormComplete = () =>
  exercises.every(
    (exercise) =>
      exercise.type &&
      exercise.time.hour &&
      exercise.time.minute &&
      exercise.time.period &&
      exercise.duration.hour &&
      exercise.duration.minute
  );

  const formatTime = (time: {
    hour: string;
    minute: string;
    period: string;
  }) => {
    if (!time.hour || !time.minute || !time.period) return "";
    return `${time.hour}:${time.minute} ${time.period.toLowerCase()}`;
  };

  const formatDuration = (duration: { hour: string; minute: string }) => {
    if (!duration.hour && !duration.minute) return "";
    const hours = duration.hour || "00";
    const minutes = duration.minute || "00";
    if (hours === "00") return `${minutes} min`;
    if (minutes === "00") return `${hours} hr`;
    return `${hours}:${minutes} hr`;
  };

  const handleSendToClient = async () => {
    if (!isFormComplete()) {
      onShowToast?.(
        "warning",
        "Incomplete form",
        "Please select exercise type for all entries"
      );
      return;
    }

    setIsCreating(true);

    try {
      // Build API payload — exercise_id now comes from the API response
      const payload = {
        nutrist_id: nutristId,
        exercise_status: "2", // Always sent as "2" (Active)
        exercises: exercises.map((exercise) => ({
          exercise_id: exercise.exerciseId,       // ← API-sourced exercise_id
          exercise_type: exercise.type,
          starting_time_hour: exercise.time.hour || "0",
          starting_time_minute: exercise.time.minute || "0",
          starting_time_period: exercise.time.period || "AM",
          duration_hours: exercise.duration.hour || "0",
          duration_minutes: exercise.duration.minute || "0",
          level_value: exercise.levelValue,
          total_calories: exercise.totalCalories,
          comment: exercise.comment,
        })),
      };

      const json = await createExercise(userId, payload);

      if (json.success === "1") {
        sendPlanNotificationToUser(userId, "exercise_plan");

        // Build optimistic local plan from first exercise
        const newExercisePlan = {
          id: Date.now(),
          plan_name: exercises[0].type,
          created_by: "You",
          created_on: new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          type: exercises[0].type,
          status: "Active" as const,
          time: formatTime(exercises[0].time),
          duration: formatDuration(exercises[0].duration),
          exercises,
        };

        onSubmit(newExercisePlan);
        handleClose();
        onShowToast?.(
          "success",
          "Exercise plan created",
          json.message || "Your exercise plan has been successfully created"
        );
      } else {
        onShowToast?.(
          "error",
          "Failed to create plan",
          json.message || "Something went wrong. Please try again."
        );
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      onShowToast?.("error", "Error", message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setExercises([
      {
        id: "1",
        exerciseId: null,
        type: "",
        time: { hour: "", minute: "", period: "" },
        duration: { hour: "", minute: "" },
        levelValue: "",
        totalCalories: "",
        comment: "",
      },
    ]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="pt-5 py-3 mx-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
            Create exercise plan
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Fixed column headers */}
        <div className="px-6 pt-5 pb-1 flex-shrink-0">
          <div className="grid grid-cols-[4fr_4fr_3fr_auto] gap-6">
            <div className="text-sm font-medium font-inter text-[#1F1F1F]">
              Exercise type
            </div>
            <div className="text-sm font-medium font-inter text-[#1F1F1F]">
              Time
            </div>
            <div className="text-sm font-medium font-inter text-[#1F1F1F]">
              Duration
            </div>
            {/* Spacer to align with delete button column */}
            <div className="w-5" />
          </div>
        </div>

        {/* Scrollable rows */}
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <div className="space-y-3 pt-3">
            {exercises.map((exercise) => (
              <ExerciseRow
                key={exercise.id}
                exercise={exercise}
                totalRows={exercises.length}
                exerciseTypeOptions={exerciseTypeOptions}
                isFetchingTypes={isFetchingTypes}
                hourOptions={hourOptions}
                minuteOptions={minuteOptions}
                periodOptions={periodOptions}
                durationHourOptions={durationHourOptions}
                durationMinuteOptions={durationMinuteOptions}
                onExerciseChange={handleExerciseChange}
                onRemove={handleRemoveExercise}
                onShowToast={onShowToast}
              />
            ))}
          </div>

          {/* Add more button */}
          <button
            onClick={handleAddMore}
            className="flex items-center gap-2 text-[#0DB5B5] text-sm font-medium hover:text-[#0CA0A0] transition-colors mt-4 mb-2"
          >
            <span className="text-lg">+</span>
            <span>Add more</span>
          </button>
        </div>

        {/* Footer */}
        <div className="py-4 mx-6 border-t border-gray-200 flex items-center justify-end gap-4 flex-shrink-0">
          <Button
            variant="secondary"
            size="md"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSendToClient}
            disabled={isCreating || !isFormComplete()}
          >
            {isCreating && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isCreating ? "Sending..." : "Send to client"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExercisePlanCreatePopup;